import * as React from 'react';
import classnames from 'classnames';

import { IPropertyBarProps } from '../type';
import { Style, FillType, StrokeType } from '../../../../models/style';

interface RotationApi {
  get_selection_rotation?: () => number;
  update_selection_rotation?: (rotation: number) => void;
}

const formatRotation = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '0';
  }
  return Number(value.toFixed(2)).toString();
};

const fillTypeOptions = [
  { label: 'None', value: FillType.None },
  { label: 'Solid', value: FillType.Solid },
  { label: 'Hatch', value: FillType.Hatch },
  { label: 'Cross', value: FillType.Cross },
];

const strokeTypeOptions = [
  { label: 'None', value: StrokeType.None },
  { label: 'Solid', value: StrokeType.Solid },
  { label: 'Dashed', value: StrokeType.Dashed },
  { label: 'Dotted', value: StrokeType.Dotted },
];

const ShapeProperties = ({ app, onStyleChange }: IPropertyBarProps) => {
  const style = app.get_selection_style() as Style;
  const rotationApi = app as unknown as RotationApi;
  const selectionRotation = rotationApi.get_selection_rotation?.() ?? 0;
  const [rotation, setRotation] = React.useState(formatRotation(selectionRotation));

  React.useEffect(() => {
    setRotation(formatRotation(selectionRotation));
  }, [selectionRotation]);

  const commitRotation = () => {
    const next = Number.parseFloat(rotation);
    rotationApi.update_selection_rotation?.(Number.isFinite(next) ? next : selectionRotation);
    onStyleChange?.();
  };

  return (
    <>
      <div className="prop-items-group">
        <div className="prop-section-title">Transform</div>
        <div className={classnames('prop-item', 'prop-rotate')}>
          <label htmlFor="shape-rotate">Rotate</label>
          <input
            id="shape-rotate"
            type="number"
            step={1}
            value={rotation}
            onChange={e => setRotation(e.target.value)}
            onBlur={commitRotation}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                commitRotation();
              }
            }}
          />
        </div>
      </div>
      <div className="prop-items-group">
        <div className="prop-section-title">Fill</div>
        <div className={classnames('prop-item', 'prop-fill-type')}>
          <div className="flat-options" role="radiogroup" aria-label="Fill Type">
            {fillTypeOptions.map(option => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={style.fill_type === option.value}
                className={classnames('flat-option', {
                  'flat-option-active': style.fill_type === option.value,
                })}
                onClick={() => {
                  app.update_selection_style({
                    fill_type: option.value,
                  });
                  onStyleChange?.();
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div
          className={classnames('prop-item', 'prop-fill-color', {
            'prop-item-disabled': style.fill_type === FillType.None,
          })}
        >
          <input
            id="fill-color"
            type="color"
            title="Fill Color"
            aria-label="Fill Color"
            disabled={style.fill_type === FillType.None}
            value={style.fill_color.to_rgb_hex()}
            onChange={e => {
              app.update_selection_style({
                fill_color: e.target.value,
              });
              onStyleChange?.();
            }}
          />
        </div>
      </div>
      <div className="prop-items-group">
        <div className="prop-section-title">Stroke</div>
        <div className={classnames('prop-item', 'prop-stroke-type')}>
          <div className="flat-options" role="radiogroup" aria-label="Stroke Type">
            {strokeTypeOptions.map(option => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={style.stroke_type === option.value}
                className={classnames('flat-option', {
                  'flat-option-active': style.stroke_type === option.value,
                })}
                onClick={() => {
                  app.update_selection_style({
                    stroke_type: option.value,
                  });
                  onStyleChange?.();
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div
          className={classnames('prop-item', 'prop-stroke-color', {
            'prop-item-disabled': style.stroke_type === StrokeType.None,
          })}
        >
          <input
            id="stroke-color"
            type="color"
            title="Stroke Color"
            aria-label="Stroke Color"
            value={style.stroke_color.to_rgb_hex()}
            disabled={style.stroke_type === StrokeType.None}
            onChange={e => {
              app.update_selection_style({
                stroke_color: e.target.value,
              });
              onStyleChange?.();
            }}
          />
        </div>
      </div>
    </>
  );
};

export default ShapeProperties;
